import React, { PureComponent } from 'react'
import { AggregationOptions } from '../../components/aggregations'
import debounce from 'lodash.debounce'
import {
  OnGroupToggleProps,
  OnOptionToggleProps,
  OnMultiGroupToggleProps
} from '../../legacy-design-system/filter'
import {
  RouteContext,
  RouteContextValue
} from '../../components/route-provider'
import { isApplicationMetadataFacet } from '../../components/value-reporting/header/filters/vr-facet-name-helper'

export interface FilterManagerChildProps<TAggregationCategory extends string> {
  filterText: string
  onFilterTextChange: (value: string) => void
  quickFilterID: string
  onQuickFilterIDChange: (quickFilterID: string) => void
  sessionMetadataFieldID?: string
  onSessionMetadataFieldIDChange: (sessionMetadataFieldID: string) => void
  onMultiGroupToggle: (
    props: OnMultiGroupToggleProps<TAggregationCategory>
  ) => void
  onGroupToggle: (props: OnGroupToggleProps<TAggregationCategory>) => void
  onOptionToggle: (props: OnOptionToggleProps<TAggregationCategory>) => void
  aggregationSelections: AggregationOptions<TAggregationCategory>
  onAggregationCategoriesClear: (categories: TAggregationCategory[]) => void
  resetFilters: () => void
}

type AggregationsToInclude =
  | 'agentSessionId'
  | 'applicationMetadata'
  | 'openVulnerabilitySeverity'
  | 'agentLanguage'
  | 'serverEnvironment'
  | 'applicationTechnologies'
  | 'applicationImportance'
  | 'languages'
  | 'servers'
  | 'openVulnSeverities'
  | 'technologies'
  | 'agentVersions'
  | 'logLevels'
  | 'environments'
  | 'applications'
  | 'grades'
  | 'scores'
  | 'tags'
  | 'licenses'
  | 'status'
  | 'usage'
  | 'severities'
  | 'types'
  | 'urls'
  | 'sinks'
  | 'statuses'
  | 'substatus'
  | 'routes'
  | 'applicationTags'
  | 'modules'
  | 'buildNumbers'
  | 'compliance'
  | 'securityStandards'
  | 'bugtrackerStatuses'
  | 'protectStatuses'
  | 'environment'
  | 'appImportances'
  | 'applicationImportances'

/**
 *  Aggregation keys need to be removed if they are not included in this list
 *  @see filtering-readme.md
 */

const aggregationsToInclude: AggregationsToInclude[] = [
  'agentSessionId',
  'applicationMetadata',
  'openVulnerabilitySeverity',
  'agentLanguage',
  'serverEnvironment',
  'applicationTechnologies',
  'applicationImportance',
  'languages',
  'servers',
  'openVulnSeverities',
  'technologies',
  'agentVersions',
  'logLevels',
  'environments',
  'applications',
  'grades',
  'scores',
  'tags',
  'licenses',
  'status',
  'usage',
  'severities',
  'types',
  'urls',
  'sinks',
  'statuses',
  'substatus',
  'routes',
  'applicationTags',
  'modules',
  'buildNumbers',
  'compliance',
  'securityStandards',
  'bugtrackerStatuses',
  'protectStatuses',
  'environment',
  'appImportances',
  'applicationImportances'
]

const shouldRemoveAggregation = (aggregation: string) =>
  !aggregationsToInclude.includes(aggregation as AggregationsToInclude) &&
  !isApplicationMetadataFacet(aggregation)

type RouteType =
  | 'libraries'
  | 'vulns'
  | 'applications'
  | 'servers'
  | 'attacks'
  | 'events-new'
  | 'library-usage'
  | 'route-coverage'

type RoutePositionType = Record<number, RouteType | undefined>

type FilterTypeRoutePosition = Record<FilterType, RoutePositionType>

export const expectedRoutes: FilterTypeRoutePosition = {
  vulns: { 1: 'vulns' },
  servers: { 1: 'servers', 2: undefined },
  libraries: { 1: 'libraries' },
  applications: { 1: 'applications', 2: undefined },
  'applications-vulns': { 1: 'applications', 3: 'vulns' },
  'servers-vulns': { 1: 'servers', 3: 'vulns' },
  'attack-events': { 1: 'attacks', 2: 'events-new' },
  'library-usage': { 1: 'library-usage' },
  'application-routes': { 1: 'applications', 3: 'route-coverage' }
}

export type FilterType =
  | 'libraries'
  | 'vulns'
  | 'applications'
  | 'servers'
  | 'applications-vulns'
  | 'servers-vulns'
  | 'attack-events'
  | 'library-usage'
  | 'application-routes'

interface ManagerProps<TAggregationCategory extends string> {
  children: (
    props: FilterManagerChildProps<TAggregationCategory>
  ) => React.ReactNode
  defaultQuickFilterId?: string
  filterType: FilterType
}

export function convertAggregationsMap<TAggregationCategory extends string>(
  map: Map<string, string[]>
): AggregationOptions<TAggregationCategory> {
  const selections: AggregationOptions<string> = {}

  map.forEach((value, key) => {
    if (!shouldRemoveAggregation(key)) {
      selections[key] = value
    }
  })

  return selections
}
export interface IsAllSelectedProps {
  queryParameters: Map<string, string[]>
  groupID: string
  optionsAvailableForSelection: string[]
}

export function isAllSelected({
  queryParameters,
  groupID,
  optionsAvailableForSelection
}: IsAllSelectedProps) {
  const selected = queryParameters.get(groupID)
  const selectionsLength = selected ? selected.length : 0
  const availableLength = optionsAvailableForSelection.length
  return selectionsLength === availableLength
}

export function onGroupToggleFactory<TAggregationCategory extends string>({
  queryParameters,
  filterType,
  setQueryParameter
}: {
  queryParameters: Map<string, string[]>
  filterType: FilterType
  setQueryParameter: RouteContextValue['setQueryParameter']
}) {
  return function onGroupToggle({
    groupID,
    optionsAvailableForSelection
  }: OnGroupToggleProps<TAggregationCategory>) {
    const allSelected = isAllSelected({
      queryParameters,
      groupID,
      optionsAvailableForSelection
    })
    if (allSelected) {
      // Deselect all
      setQueryParameter(filterType, groupID, [])
    } else {
      // Select all
      setQueryParameter(filterType, groupID, optionsAvailableForSelection)
    }
  }
}

// Checks if all filters are selected from multiple groups.
// If all filters are selected, deselect all filters. Otherwise, select all filters.
export function onMultiGroupToggleFactory<TAggregationCategory extends string>({
  queryParameters,
  filterType,
  setQueryParameters
}: {
  queryParameters: Map<string, string[]>
  filterType: FilterType
  setQueryParameters: RouteContextValue['setQueryParameters']
}) {
  return function onMultiGroupToggle({
    groupsOfAvailableOptions
  }: OnMultiGroupToggleProps<TAggregationCategory>) {
    const groupIDs = Array.from(groupsOfAvailableOptions.keys())
    const allSelected = groupIDs.every((groupID) => {
      const optionsAvailableForSelection = groupsOfAvailableOptions.get(groupID)
      return optionsAvailableForSelection
        ? isAllSelected({
            queryParameters,
            groupID,
            optionsAvailableForSelection
          })
        : false
    })

    if (allSelected) {
      // Deselect all
      groupIDs.forEach((groupID) => {
        groupsOfAvailableOptions.set(groupID, [])
      })
      setQueryParameters(filterType, groupsOfAvailableOptions)
    } else {
      // Select all
      setQueryParameters(filterType, groupsOfAvailableOptions)
    }
  }
}

export class FilterManager<
  TAggregationCategory extends string
> extends PureComponent<ManagerProps<TAggregationCategory>> {
  constructor(props: ManagerProps<TAggregationCategory>) {
    super(props)
  }

  public render() {
    return (
      <RouteContext.Consumer>
        {({
          queryParameters,
          setQueryParameter,
          setQueryParameters,
          toggleQueryParameter,
          clear,
          clearAll,
          parameters
        }) => {
          const { defaultQuickFilterId, filterType, children } = this.props
          return (
            <FilterManagerContent
              children={children}
              queryParameters={queryParameters}
              setQueryParameter={setQueryParameter}
              setQueryParameters={setQueryParameters}
              toggleQueryParameter={toggleQueryParameter}
              clear={clear}
              routeParameters={parameters}
              clearAll={clearAll}
              defaultQuickFilterId={defaultQuickFilterId}
              filterType={filterType}
            />
          )
        }}
      </RouteContext.Consumer>
    )
  }
}

interface FilterManagerContentProps<TAggregationCategory extends string> {
  children: (
    props: FilterManagerChildProps<TAggregationCategory>
  ) => React.ReactNode

  filterType: FilterType
  defaultQuickFilterId?: string
  queryParameters: Map<string, string[]>
  setQueryParameter: RouteContextValue['setQueryParameter']
  setQueryParameters: RouteContextValue['setQueryParameters']
  toggleQueryParameter: (
    filtertype: FilterType,
    parameter: string,
    value: string
  ) => void
  clear: (filtertype: FilterType, parameters: string[] | string) => void
  clearAll: (filtertype: FilterType) => void
  routeParameters: string[]
}

interface FilterManagerContentState<TAggregationCategory extends string> {
  sessionMetadataFieldID?: string
  aggregationOptions?: AggregationOptions<TAggregationCategory>
}

export class FilterManagerContent<
  TAggregationCategory extends string
> extends PureComponent<
  FilterManagerContentProps<TAggregationCategory>,
  FilterManagerContentState<TAggregationCategory>
> {
  private convertAggregationsMap = convertAggregationsMap

  constructor(props: FilterManagerContentProps<TAggregationCategory>) {
    super(props)

    this.state = {}
  }

  public render() {
    const { sessionMetadataFieldID } = this.state
    const {
      filterType,
      defaultQuickFilterId,
      queryParameters,
      setQueryParameter,
      setQueryParameters,
      toggleQueryParameter,
      clear,
      clearAll
    } = this.props

    const quickFilterArray: string[] | undefined =
      queryParameters.get('quickFilter')
    const quickFilterID: string = quickFilterArray
      ? quickFilterArray[0].toUpperCase()
      : ''

    const aggregationSelectionsMap: Map<string, string[]> = queryParameters
      ? queryParameters
      : new Map<string, string[]>()

    const filterTextArray: string[] | undefined =
      aggregationSelectionsMap && aggregationSelectionsMap.get('filterText')
    const filterText: string = filterTextArray ? filterTextArray[0] : ''

    return this.props.children({
      filterText,
      quickFilterID: quickFilterID
        ? quickFilterID
        : defaultQuickFilterId || 'ALL',
      sessionMetadataFieldID,
      aggregationSelections: this.convertAggregationsMap(
        aggregationSelectionsMap
          ? aggregationSelectionsMap
          : new Map<string, string[]>()
      ),
      onFilterTextChange: debounce(
        (newFilterText: string) => {
          setQueryParameter(filterType, 'filterText', newFilterText)
        },
        500,
        {}
      ),
      onQuickFilterIDChange: (newQuickFilterID: string) => {
        setQueryParameter(filterType, 'quickFilter', newQuickFilterID)
      },
      onSessionMetadataFieldIDChange: this.handleSessionMetadataFieldIDChange,
      onMultiGroupToggle: onMultiGroupToggleFactory({
        setQueryParameters,
        filterType,
        queryParameters
      }),
      onGroupToggle: onGroupToggleFactory({
        setQueryParameter,
        filterType,
        queryParameters
      }),
      onOptionToggle: ({
        groupID,
        optionID
      }: OnOptionToggleProps<TAggregationCategory>) => {
        toggleQueryParameter(filterType, groupID, optionID)
      },
      onAggregationCategoriesClear: (categories: TAggregationCategory[]) => {
        clear(filterType, categories)
      },
      resetFilters: () => {
        clearAll(filterType)
      }
    })
  }

  private handleSessionMetadataFieldIDChange = (
    sessionMetadataFieldID: string
  ) => this.setState({ sessionMetadataFieldID })
}
